import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { useNavigate } from 'react-router-dom'
import { Github, Copy, Check } from 'lucide-react'
import { Button } from './ui/button'
import { requestDeviceCode, pollForAccessToken, DeviceFlowError, type DeviceCodeResponse } from '../oauth'
import { setToken } from '../github'

type Status = 'idle' | 'requesting' | 'pending' | 'success' | 'error'

export function DeviceFlowSetup() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('idle')
  const [info, setInfo] = useState<DeviceCodeResponse | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    start()
    return () => abortRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const url = info?.verification_uri_complete ?? info?.verification_uri
    if (!url) return
    QRCode.toDataURL(url, { width: 280, margin: 2, errorCorrectionLevel: 'M' })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null))
  }, [info])

  async function start() {
    setStatus('requesting')
    setError(null)
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    try {
      const code = await requestDeviceCode()
      setInfo(code)
      setStatus('pending')
      const token = await pollForAccessToken(code.device_code, code.interval, ctrl.signal)
      setToken(token.access_token)
      setStatus('success')
      // Reload completo: garantiza que App.tsx re-evalúe hasToken() y monte Dashboard.
      window.location.replace('/')
    } catch (err) {
      if (err instanceof DeviceFlowError) {
        if (err.code === 'aborted') return // navegando fuera, no es error real
        if (err.code === 'expired_token') {
          setError('El código expiró antes de que lo confirmaras. Vamos a generar uno nuevo.')
        } else if (err.code === 'access_denied') {
          setError('Cancelaste la autorización. Reintenta o usa un PAT manual.')
        } else if (err.code === 'server_misconfig') {
          setError('OAuth no está configurado en el servidor. Usa un PAT manual abajo.')
        } else {
          setError(err.message || err.code)
        }
        setStatus('error')
      }
    }
  }

  function copyCode() {
    if (!info?.user_code) return
    navigator.clipboard.writeText(info.user_code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      role="dialog"
      aria-label="Conectar con GitHub"
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950 text-zinc-100 p-8"
    >
      <div className="max-w-3xl w-full bg-zinc-900 border border-zinc-800 rounded-xl p-10 space-y-6">
        <div className="flex items-center gap-3">
          <Github className="h-8 w-8" />
          <h1 className="text-3xl font-bold">Conectar con GitHub</h1>
        </div>

        {status === 'requesting' && (
          <p className="text-zinc-400">Generando código de pareo…</p>
        )}

        {status === 'pending' && info && (
          <div className="grid grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <p className="text-zinc-300">
                Desde tu celular o laptop, ve a{' '}
                <a
                  href={info.verification_uri}
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold underline text-blue-400"
                >
                  {info.verification_uri.replace(/^https?:\/\//, '')}
                </a>{' '}
                y escribe este código:
              </p>
              <div className="flex items-center gap-3">
                <code
                  data-testid="device-user-code"
                  className="text-5xl font-mono font-bold tracking-[0.4em] bg-zinc-950 px-6 py-4 rounded-lg select-all"
                >
                  {info.user_code}
                </code>
                <Button variant="ghost" size="icon" aria-label="Copiar código" onClick={copyCode}>
                  {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
                </Button>
              </div>
              <p className="text-sm text-zinc-500">
                Esperando a que confirmes en GitHub. Esto se cierra solo cuando autorices.
              </p>
            </div>
            <div className="flex justify-center">
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt="QR para abrir github.com/login/device en celular"
                  data-testid="device-qr"
                  className="rounded-md border border-zinc-800 bg-white p-2"
                />
              )}
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-3">
            <p role="alert" className="text-red-400">
              {error}
            </p>
            <div className="flex gap-2">
              <Button onClick={start}>Reintentar</Button>
              <Button variant="outline" onClick={() => navigate('/setup-pat')}>
                Usar PAT manual
              </Button>
            </div>
          </div>
        )}

        <div className="border-t border-zinc-800 pt-4 text-sm">
          <button
            onClick={() => navigate('/setup-pat')}
            className="text-zinc-500 hover:text-zinc-300 underline"
          >
            ¿Prefieres pegar un PAT manualmente?
          </button>
        </div>
      </div>
    </div>
  )
}
